/*
 * Helpers for filtering a package's selected staterooms by group cabin
 * availability (derived from the group/{id} endpoint's grpCategoryAllocInfos).
 *
 * `groupData.categoryOptions` is an array of { categoryCode, groupCabinsAvailable }
 * built from the group's allocated categories. A category is "group available"
 * only when it is present in that list with a non-zero `groupCabinsAvailable`.
 * `categoryOptions === undefined` is the only graceful fallback (no group data
 * fetched, or fetch failed) and means "don't filter."
 */

export const isCategoryGroupAvailable = (categoryOptions, categoryCode) => {
  if (!categoryCode) return true
  if (!categoryOptions) return true
  const gca = categoryOptions.find(g => g.categoryCode === categoryCode)
  return Boolean(gca) && gca.groupCabinsAvailable !== 0
}

/*
 * For a group-priced package, recompute pricing/staterooms across only the
 * categories that still have group cabins available.
 *
 * - Super-category selections are kept when at least one cabin in the super
 *   category has group availability. The price is rebuilt from the cheapest
 *   still-available cabin in `currentPackage.pricing[superCat].availableStateRooms`,
 *   because the super-cat's `bestGuestCountRate` may originate from a now-
 *   unavailable cabin.
 * - Specific stateroom selections are kept when their own category code is
 *   still group-available.
 *
 * Returns null when nothing is available. Returns the package's existing
 * rollup when groupData has no categoryOptions field (e.g. fetch failed or
 * the package isn't group-priced).
 */
export default function getGroupFilteredPricing(currentPackage, groupData) {
  const selectedStateRooms = currentPackage?.selectedStateRooms
  if (!selectedStateRooms?.stateRooms?.length) return null

  const categoryOptions = groupData?.categoryOptions
  if (!categoryOptions) {
    return {
      stateRooms: selectedStateRooms.stateRooms,
      bestGuestCountRate: selectedStateRooms.bestGuestCountRate,
      bestPerPersonAveragePrice: selectedStateRooms.bestPerPersonAveragePrice,
      pricingFrom: selectedStateRooms.pricingFrom,
      priceInfo: selectedStateRooms.priceInfo
    }
  }

  const filteredStateRooms = []
  let bestGuestCountRate = null
  let bestPerPersonAveragePrice = null
  let pricingFrom = null
  let priceInfo = null

  const consider = (netPrice, perPersonAveragePrice, pricing, info) => {
    if (
      netPrice != null &&
      (bestGuestCountRate == null || netPrice < bestGuestCountRate)
    ) {
      bestGuestCountRate = netPrice
      bestPerPersonAveragePrice = perPersonAveragePrice
      pricingFrom = pricing
      priceInfo = info
    }
  }

  selectedStateRooms.stateRooms.forEach(stateRoom => {
    if (stateRoom.isSuperCategory) {
      const availableStateRooms =
        currentPackage?.pricing?.[stateRoom.stateroomType]
          ?.availableStateRooms || []
      const availableInSuperCat = availableStateRooms.filter(room =>
        isCategoryGroupAvailable(categoryOptions, room.stateroomCategoryCode)
      )
      if (!availableInSuperCat.length) return
      filteredStateRooms.push(stateRoom)
      availableInSuperCat.forEach(room =>
        consider(
          room.netPrice,
          room.perPersonAveragePrice,
          stateRoom.pricing,
          room.priceInfo
        )
      )
    } else if (
      isCategoryGroupAvailable(
        categoryOptions,
        stateRoom.stateroomCategoryCode || stateRoom.category
      )
    ) {
      filteredStateRooms.push(stateRoom)
      consider(
        stateRoom.netPrice,
        stateRoom.perPersonAveragePrice,
        stateRoom.pricing,
        stateRoom.priceInfo
      )
    }
  })

  if (!filteredStateRooms.length) return null

  return {
    stateRooms: filteredStateRooms,
    bestGuestCountRate,
    bestPerPersonAveragePrice,
    pricingFrom,
    priceInfo
  }
}
